import React, { useCallback, useEffect, useRef, useState } from 'react';
import { loadGoogleMaps, placeTypeToCuisine } from '../lib/googlePlaces';

export interface PlaceSelection {
  placeId: string;
  name: string;
  cuisine: string;
  location: string;
}

interface PlaceAutocompleteProps {
  onSelect: (place: PlaceSelection) => void;
  disabled?: boolean;
  placeholder?: string;
}

// Loose shape of the gmp-select event payload + Place object. We avoid
// pulling in @types/google.maps just to type these few properties; the
// wrapper code touches only the documented fields.
interface GooglePlace {
  id?: string;
  displayName?: string;
  formattedAddress?: string;
  types?: string[];
  fetchFields: (options: { fields: string[] }) => Promise<unknown>;
}

interface GmpSelectEvent extends Event {
  placePrediction?: { toPlace: () => GooglePlace };
}

// The host <gmp-place-autocomplete> element gets its border / radius /
// background / focus ring from CSS in index.css. The wrapper just acts as
// the mount point and shows a subtle placeholder while Maps is loading.
const containerClass = 'w-full min-h-[3rem]';

const PlaceAutocomplete: React.FC<PlaceAutocompleteProps> = ({
  onSelect,
  disabled,
  placeholder,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const onSelectRef = useRef(onSelect);
  const disabledRef = useRef(disabled);
  const [error, setError] = useState<string | null>(null);
  // Bumped after each successful selection. The web component offers no API
  // to clear its input, so leaving it mounted strands the picked restaurant's
  // name in the field; remounting a fresh element resets it for the next add.
  const [generation, setGeneration] = useState(0);

  // Keep the latest onSelect/disabled in refs so the effect's listener stays stable.
  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);
  useEffect(() => {
    disabledRef.current = disabled;
  }, [disabled]);

  const handleSelect = useCallback(async (event: GmpSelectEvent) => {
    if (disabledRef.current) return;
    const place = event.placePrediction?.toPlace();
    if (!place) return;
    try {
      await place.fetchFields({
        fields: ['id', 'displayName', 'formattedAddress', 'types'],
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to load place details', err);
      setError('Restaurant search hit a snag. Please try selecting again.');
      return;
    }
    if (!place.id || !place.displayName) return;
    onSelectRef.current({
      placeId: place.id,
      name: place.displayName,
      cuisine: placeTypeToCuisine(place.types),
      location: place.formattedAddress ?? '',
    });
    setGeneration((g) => g + 1);
  }, []);

  useEffect(() => {
    let mounted = true;
    let element: HTMLElement | null = null;

    loadGoogleMaps()
      .then(() => {
        if (!mounted || !containerRef.current) return;
        element = document.createElement('gmp-place-autocomplete');
        // gmp-place-autocomplete is a custom element; these property
        // assignments configure its behavior.
        // Filters on the place's *primary* type only. `brewery` covers
        // brewpubs/cideries that serve food (Google often makes that their
        // primary type even when `restaurant` is a secondary type). The
        // Autocomplete API caps this list at 5 entries — it's full.
        (element as unknown as { includedPrimaryTypes?: string[] }).includedPrimaryTypes = [
          'restaurant',
          'cafe',
          'bar',
          'bakery',
          'brewery',
        ];
        if (placeholder) {
          element.setAttribute('placeholder', placeholder);
        }
        element.addEventListener('gmp-select', handleSelect as EventListener);
        containerRef.current.innerHTML = '';
        containerRef.current.appendChild(element);
        // On a post-selection remount, put the cursor back so the user can
        // type their next restaurant without re-clicking the field.
        if (generation > 0) {
          element.focus();
        }
      })
      .catch((err: Error) => {
        // eslint-disable-next-line no-console
        console.error('Failed to load Google Maps', err);
        if (mounted) {
          setError(
            'Restaurant search is temporarily unavailable. Please refresh to try again.'
          );
        }
      });

    return () => {
      mounted = false;
      if (element) {
        element.removeEventListener('gmp-select', handleSelect as EventListener);
      }
    };
  }, [handleSelect, placeholder, generation]);

  if (error) {
    return (
      <p className="text-sm text-terracotta" role="alert">
        {error}
      </p>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`${containerClass}${disabled ? ' opacity-50 pointer-events-none' : ''}`}
      aria-label={placeholder ?? 'Search for a restaurant'}
      aria-disabled={disabled ? 'true' : undefined}
      data-disabled={disabled ? 'true' : undefined}
    />
  );
};

export default PlaceAutocomplete;
